import React, { useCallback, useMemo, useState } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { supabase, useAuth } from 'hooks/useAuth';
import { useMemberGroupData } from 'hooks/useMemberGroupData';
import { useToast } from 'hooks/useToast';

import { VALUES } from 'libs/constants';
import { Database } from 'libs/supabaseTypes';

import { SteppedForm } from 'components/layout/SteppedForm';

import { TextInput } from 'components/ui/TextInput';
import { Button } from 'components/ui/Button';

import { colors, Fonts, spacing } from 'styles/theme';
import {
    Bumper,
    Checkbox,
    CheckboxWrapper,
    NestedCard,
    GridListWrapper,
    BackButtonWrapper,
} from 'styles/shared';
import { useResponsiveScreenSizes } from 'hooks/useResponsiveScreenSizes';
import { ValuesImageUpload } from './ValuesImageUpload';

type Json = Database['public']['Tables']['values']['Insert']['value_groups'];

interface SubmitValuesProps {
    onChangeFormStep: () => void;
    onClose: () => void;
}

const AVAILABLE_COLORS = [
    colors.pursuitBlue,
    colors.pursuitBrown,
    colors.pursuitGold,
    colors.pursuitOrange,
    colors.pursuitGreen,
];

type PursuitColor = (typeof AVAILABLE_COLORS)[number];

export interface ValueGroup {
    color: PursuitColor;
    values: string[];
    phrase: string;
    ranking: number; // 1..5, 0 means unset
}

interface SortableValueGroup extends ValueGroup {
    id: string;
}

interface SortableItemProps {
    group: SortableValueGroup;
}

interface ShelfLabelProps {
    label: string;
    description: string;
    color: string;
    emoji: string;
}

function normalizeRankingsInState(groups: ValueGroup[]): ValueGroup[] {
    // Only consider groups that are "in play" (have a label/phrase)
    const withPhrase = groups.filter((g) => g.phrase.trim() !== '');
    if (withPhrase.length !== 5) return groups;

    // Already valid? (every ranking is an integer 1..5 and unique)
    const validSet = new Set(withPhrase.map((g) => g.ranking));
    const alreadyValid =
        withPhrase.every(
            (g) =>
                Number.isInteger(g.ranking) && g.ranking >= 1 && g.ranking <= 5
        ) && validSet.size === 5;

    if (alreadyValid) return groups;

    // Use current visual order (stable order of groups array filtered by phrase)
    const order = withPhrase.map((g) => g.color);
    const rankingMap = new Map(order.map((color, i) => [color, i + 1]));

    const next = groups.map((g) =>
        rankingMap.has(g.color)
            ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              { ...g, ranking: rankingMap.get(g.color)! }
            : g
    );

    return next;
}

const ShelfLabel = ({ label, description, color, emoji }: ShelfLabelProps) => {
    return (
        <div
            style={{
                padding: '8px',
                backgroundColor: colors.white,
                borderBottom: `2px solid ${color}`,
                borderRadius: '4px',
                marginBottom: '8px',
            }}
        >
            <Fonts.Medium style={{ color, fontWeight: 'bold' }}>
                {emoji} {label}
            </Fonts.Medium>
            <Bumper $minHeight={spacing.xxxSmall} />
            <Fonts.Small style={{ color: '#666', marginTop: '4px' }}>
                {description}
            </Fonts.Small>
        </div>
    );
};

const SortableItem = ({ group }: SortableItemProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: group.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <NestedCard
            ref={setNodeRef}
            style={{
                ...style,
                cursor: 'grab',
                // Important for mobile gestures:
                touchAction: 'none',
            }}
            {...attributes}
            {...listeners}
        >
            <Fonts.Small $color={colors.darkJungle}>{group.phrase}</Fonts.Small>
        </NestedCard>
    );
};

interface ColorPopupProps {
    value: string;
    onClose: () => void;
    onSelectColor: (color: string) => void;
    currentColor?: string;
    position?: { top: number; left: number };
}

const ColorPopup = ({
    value,
    onClose,
    onSelectColor,
    currentColor,
    position,
}: ColorPopupProps) => {
    return (
        <>
            {/* Backdrop */}
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 999,
                }}
                onClick={onClose}
                onKeyDown={(e) => e.key === 'Escape' && onClose()}
                role="button"
                tabIndex={0}
            />

            {/* Popup positioned above the button */}
            <div
                style={{
                    position: 'fixed',
                    top: position ? position.top - 120 : '50%',
                    left: position ? position.left - 100 : '50%',
                    transform: position ? 'none' : 'translate(-50%, -50%)',
                    zIndex: 1000,
                    width: '240px',
                }}
                role="dialog"
                tabIndex={-1}
            >
                <NestedCard
                    style={{
                        backgroundColor: 'white',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        position: 'relative',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    role="dialog"
                    tabIndex={-1}
                >
                    {/* Arrow pointing down */}
                    <div
                        style={{
                            position: 'absolute',
                            bottom: '-8px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '8px solid transparent',
                            borderRight: '8px solid transparent',
                            borderTop: '8px solid white',
                        }}
                        role="presentation"
                    />

                    <Fonts.Medium>
                        Choose a color for &ldquo;{value}&rdquo;
                    </Fonts.Medium>
                    <Bumper />
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                        }}
                    >
                        {AVAILABLE_COLORS.map((color) => (
                            <Button
                                key={color}
                                onClick={() => {
                                    onSelectColor(color);
                                    onClose();
                                }}
                                $style="outline"
                                $isSmall
                                style={{
                                    backgroundColor:
                                        currentColor === color
                                            ? color
                                            : 'transparent',
                                    borderColor: color,
                                    color:
                                        currentColor === color
                                            ? 'white'
                                            : color,
                                }}
                            >
                                ●
                            </Button>
                        ))}
                    </div>
                </NestedCard>
            </div>
        </>
    );
};

export const SubmitValues = ({
    onChangeFormStep,
    onClose,
}: SubmitValuesProps) => {
    const { userId } = useAuth();
    const { getValues } = useMemberGroupData();
    const { showSuccessToast, showErrorToast } = useToast();
    const { isMobileScreenSize } = useResponsiveScreenSizes();

    const [isUploadingImages, setIsUploadingImages] = useState<boolean>(false);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [customValue, setCustomValue] = useState<string>('');
    const [valueGroups, setValueGroups] = useState<ValueGroup[]>([]);
    const [isSubmittingValues, setIsSubmittingValues] =
        useState<boolean>(false);
    const [popupValue, setPopupValue] = useState<string | null>(null);
    const [popupPosition, setPopupPosition] = useState<{
        top: number;
        left: number;
    } | null>(null);

    const onToggleImageUpload = useCallback(() => {
        setIsUploadingImages((prev) => !prev);
    }, []);

    // Sensor with mobile-friendly activation constraint
    const sensors = useSensors(
        useSensor(PointerSensor, {
            // Helps prevent accidental scroll/tap conflicts on touch
            activationConstraint: { distance: 8 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Derive a stable, rendered list and reuse for SortableContext.items and rendering
    const rankedGroups = useMemo(() => {
        const groups = valueGroups.filter((g) => g.phrase.trim() !== '');
        // Ensure deterministic initial ranking (if 0, assign by current index)
        const normalized = groups.map((g, idx) => ({
            ...g,
            ranking: g.ranking || idx + 1,
        }));
        return normalized.sort((a, b) => a.ranking - b.ranking);
    }, [valueGroups]);

    const onToggleValue = useCallback((value: string) => {
        setSelectedValues((prev) => {
            if (prev.includes(value)) {
                return prev.filter((v) => v !== value);
            } else {
                return [...prev, value];
            }
        });
    }, []);

    const onAddCustomValue = useCallback(() => {
        if (
            customValue.trim() &&
            !selectedValues.includes(customValue.trim())
        ) {
            setSelectedValues((prev) => [...prev, customValue.trim()]);
            setCustomValue('');
        }
    }, [customValue, selectedValues]);

    const onCustomValueInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setCustomValue(target.value);
        },
        []
    );

    const onGroupValue = useCallback((value: string, color: PursuitColor) => {
        setValueGroups((prev) => {
            // Remove value from all existing groups first
            const clearedGroups = prev.map((group) => ({
                ...group,
                values: group.values.filter((v) => v !== value),
            }));

            // Find the target group
            const targetGroup = clearedGroups.find(
                (group) => group.color === color
            );

            if (targetGroup) {
                // Add value to existing group
                return clearedGroups.map((group) =>
                    group.color === color
                        ? { ...group, values: [...group.values, value] }
                        : group
                );
            } else {
                // Create new group
                return [
                    ...clearedGroups,
                    { color, values: [value], phrase: '', ranking: 0 },
                ];
            }
        });
    }, []);

    const onOpenColorPopup = useCallback(
        (value: string, event: React.MouseEvent<HTMLButtonElement>) => {
            const rect = event.currentTarget.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const desiredLeft = rect.left + rect.width / 2;
            const maxLeft = viewportWidth - 140;
            const clampedLeft = Math.min(desiredLeft, maxLeft);
            setPopupPosition({
                top: rect.top,
                left: clampedLeft,
            });
            setPopupValue(value);
        },
        []
    );

    const onCloseColorPopup = useCallback(() => {
        setPopupValue(null);
        setPopupPosition(null);
    }, []);

    const onSelectColor = useCallback(
        (color: string) => {
            if (popupValue) {
                onGroupValue(popupValue, color as PursuitColor);
            }
        },
        [popupValue, onGroupValue]
    );

    const getValueColor = useCallback(
        (value: string) => {
            const group = valueGroups.find((g) => g.values.includes(value));
            return group?.color || 'transparent';
        },
        [valueGroups]
    );

    const onPhraseInput = useCallback(
        (color: string) => (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setValueGroups((prev) =>
                prev.map((group) =>
                    group.color === color
                        ? { ...group, phrase: target.value }
                        : group
                )
            );
        },
        []
    );

    // Updated handleDragEnd: use rankedGroups for indices, then persist both ranking and array order
    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setValueGroups((prev) => {
            // Work from a normalized, sorted list of the ranked subset
            const subset = prev
                .filter((g) => g.phrase.trim() !== '')
                .map((g, idx) => ({ ...g, ranking: g.ranking || idx + 1 }))
                .sort((a, b) => a.ranking - b.ranking);

            const oldIndex = subset.findIndex((g) => g.color === active.id);
            const newIndex = subset.findIndex((g) => g.color === over.id);
            if (oldIndex < 0 || newIndex < 0) return prev;

            const moved = arrayMove(subset, oldIndex, newIndex);

            // Map new rankings back
            const rankingMap = new Map(moved.map((g, i) => [g.color, i + 1]));

            const next = prev.map((g) =>
                rankingMap.has(g.color)
                    ? { ...g, ranking: rankingMap.get(g.color) || 0 }
                    : g
            );

            // Reorder underlying array to keep it consistent with rankings for the ranked subset
            const ranked = next
                .filter((g) => g.phrase.trim() !== '')
                .sort((a, b) => (a.ranking || 9999) - (b.ranking || 9999));

            const unranked = next.filter((g) => g.phrase.trim() === '');

            // Keep unranked (no phrase) at the bottom in original relative order
            return [...ranked, ...unranked];
        });
    }, []);

    const onSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (isSubmittingValues) {
                return;
            }
            setIsSubmittingValues(true);

            const safeGroups = normalizeRankingsInState(valueGroups);

            const { error } = await supabase
                .from('values')
                .insert({
                    created_by: userId ?? '',
                    value_groups: safeGroups as unknown as Json,
                })
                .select();

            if (error == null) {
                getValues?.();
                showSuccessToast('Your values have been submitted!');
            } else {
                showErrorToast(error.message);
            }

            setIsSubmittingValues(false);
        },
        [
            getValues,
            isSubmittingValues,
            showErrorToast,
            showSuccessToast,
            userId,
            valueGroups,
        ]
    );

    const headerNode = useMemo(
        () => (
            <>
                <Bumper $minHeight={spacing.small} $shouldHideOnDesktop />
                <Fonts.Heading5>{`Values Exercise`}</Fonts.Heading5>
                <Bumper />
            </>
        ),
        []
    );

    const formSteps = useMemo(() => {
        const formStepsReturnArray = [
            {
                isComplete: true,
                formNode: (
                    <>
                        <Button
                            onClick={onToggleImageUpload}
                            $style="outline"
                            $isSmall
                        >{`Switch to Image Uploader`}</Button>
                        <Bumper $minHeight={spacing.xLarge} />
                        <NestedCard>
                            <Fonts.Medium>
                                {`From the list, select every value that resonates with you. Do not overthink your selection. As you read through the list, simply check off the words that feel like a core value to you personally. If you think of a value you possess that is not on the list, add it via the text input at the bottom.`}
                            </Fonts.Medium>
                        </NestedCard>
                        <Bumper />
                        <GridListWrapper
                            $gap={spacing.medium}
                            style={
                                isMobileScreenSize
                                    ? {
                                          flexWrap: 'nowrap',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                      }
                                    : {}
                            }
                        >
                            {VALUES.map((value) => (
                                <div
                                    key={value}
                                    style={
                                        isMobileScreenSize
                                            ? {}
                                            : { width: '180px' }
                                    }
                                >
                                    <CheckboxWrapper>
                                        <Checkbox
                                            type="checkbox"
                                            checked={selectedValues.includes(
                                                value
                                            )}
                                            onChange={() =>
                                                onToggleValue(value)
                                            }
                                        />
                                        <Fonts.Small>{value}</Fonts.Small>
                                    </CheckboxWrapper>
                                </div>
                            ))}
                            {selectedValues
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                .filter((value) => !VALUES.includes(value))
                                .map((value) => (
                                    <div
                                        key={value}
                                        style={
                                            isMobileScreenSize
                                                ? {}
                                                : { width: '180px' }
                                        }
                                    >
                                        <CheckboxWrapper>
                                            <Checkbox
                                                type="checkbox"
                                                checked={selectedValues.includes(
                                                    value
                                                )}
                                                onChange={() =>
                                                    onToggleValue(value)
                                                }
                                            />
                                            <Fonts.Small>{value}</Fonts.Small>
                                        </CheckboxWrapper>
                                    </div>
                                ))}
                        </GridListWrapper>
                        <Bumper />
                        <NestedCard>
                            <TextInput
                                id="customValue"
                                name="customValue"
                                value={customValue}
                                onInput={onCustomValueInput}
                                label="Add your own value"
                                placeholder="Enter a custom value"
                            />
                            <Bumper />
                            <Button
                                onClick={onAddCustomValue}
                                $style="outline"
                                $isSmall
                                $isDisabled={!customValue.trim()}
                            >
                                Add Value to List
                            </Button>
                        </NestedCard>
                    </>
                ),
            },
        ];

        // Step: assign values to color groups (kept as-is)
        formStepsReturnArray.push({
            isComplete:
                valueGroups.filter((g) => g.phrase.trim() !== '').length === 5,
            formNode: (
                <>
                    <NestedCard>
                        <Fonts.Medium>
                            {`Now group your selected values by color. Tap a value and then select one of the colored buttons in the popup to assign it to a group. Then, once you have five groups created, select one of the values to represent the group as its label.`}
                        </Fonts.Medium>
                    </NestedCard>
                    <Bumper />
                    <GridListWrapper
                        $gap={spacing.small}
                        style={{ justifyContent: 'center' }}
                    >
                        {selectedValues.map((value) => {
                            const currentColor = getValueColor(value);
                            return (
                                <Button
                                    key={value}
                                    onClick={(event) =>
                                        onOpenColorPopup(value, event)
                                    }
                                    $style="outline"
                                    style={{
                                        backgroundColor:
                                            currentColor === 'transparent'
                                                ? 'transparent'
                                                : currentColor,
                                        borderColor:
                                            currentColor === 'transparent'
                                                ? '#ccc'
                                                : currentColor,
                                        color:
                                            currentColor === 'transparent'
                                                ? '#666'
                                                : 'white',
                                        padding: '12px 16px',
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    <Fonts.Small
                                        $color={
                                            currentColor === colors.pursuitBrown
                                                ? colors.white
                                                : undefined
                                        }
                                    >
                                        {value}
                                    </Fonts.Small>
                                </Button>
                            );
                        })}
                    </GridListWrapper>
                    <Bumper />
                    {AVAILABLE_COLORS.map((color) => {
                        const group = valueGroups.find(
                            (g) => g.color === color
                        );

                        if (!group || group.values.length === 0) return null;

                        return (
                            <>
                                <Bumper />
                                <NestedCard
                                    key={color}
                                    style={{ borderLeft: `4px solid ${color}` }}
                                >
                                    <div>
                                        <Fonts.Small
                                            style={{
                                                marginBottom: '8px',
                                                display: 'block',
                                            }}
                                        >
                                            Choose a value to represent this
                                            group:
                                        </Fonts.Small>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: '8px',
                                            }}
                                        >
                                            {group.values.map((value) => (
                                                <Button
                                                    key={value}
                                                    onClick={() =>
                                                        onPhraseInput(color)({
                                                            target: { value },
                                                        } as unknown as React.FormEvent<HTMLInputElement>)
                                                    }
                                                    $style={
                                                        group.phrase === value
                                                            ? 'fill'
                                                            : 'outline'
                                                    }
                                                    $isSmall
                                                >
                                                    {value}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                </NestedCard>
                            </>
                        );
                    })}
                </>
            ),
        });

        // UPDATED Step: turn each group into a verb phrase (map over valueGroups, edit group.phrase via TextInput)
        const groupsWithValues = valueGroups.filter((g) => g.values.length > 0);
        formStepsReturnArray.push({
            isComplete:
                groupsWithValues.length > 0 &&
                groupsWithValues.every((g) => g.phrase.trim() !== ''),
            formNode: (
                <>
                    <NestedCard>
                        <Fonts.Medium>
                            {`Now, turn the values you chose to represent each of the groups from the previous step into a phrase with a verb using the text fields below. Here are some examples:`}
                        </Fonts.Medium>
                        <Bumper $minHeight={spacing.xxSmall} />
                        <Fonts.MediumQuote>
                            &nbsp;&nbsp;&nbsp;• Live in freedom
                        </Fonts.MediumQuote>
                        <Fonts.MediumQuote>
                            &nbsp;&nbsp;&nbsp;• Seek opportunities for making a
                            difference
                        </Fonts.MediumQuote>
                        <Fonts.MediumQuote>
                            &nbsp;&nbsp;&nbsp;• Act with mindfulness
                        </Fonts.MediumQuote>
                        <Fonts.MediumQuote>
                            &nbsp;&nbsp;&nbsp;• Promote well-being
                        </Fonts.MediumQuote>
                        <Fonts.MediumQuote>
                            &nbsp;&nbsp;&nbsp;• Multiply happiness
                        </Fonts.MediumQuote>
                    </NestedCard>
                    <Bumper />
                    <GridListWrapper $gap={spacing.small}>
                        {groupsWithValues.map((group) => (
                            <NestedCard
                                key={group.color}
                                style={{
                                    borderLeft: `4px solid ${group.color}`,
                                    width: '100%',
                                }}
                            >
                                <TextInput
                                    id={`phrase-${group.color}`}
                                    name={`phrase-${group.color}`}
                                    value={group.phrase}
                                    onInput={onPhraseInput(group.color)}
                                    label="Turn this representative value into a verb phrase"
                                />
                            </NestedCard>
                        ))}
                    </GridListWrapper>
                </>
            ),
        });

        // Ranking step (unchanged logic; uses rankedGroups)
        formStepsReturnArray.push({
            isComplete:
                valueGroups.filter((g) => g.phrase.trim() !== '').length === 5,
            formNode: (
                <>
                    <NestedCard>
                        <Fonts.Medium>
                            {`Finally, drag and drop your value phrases to rank them using the shelf system below.`}
                        </Fonts.Medium>
                    </NestedCard>
                    <Bumper />

                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            // IMPORTANT: items mirrors the rendered order
                            items={rankedGroups.map((group) => group.color)}
                            strategy={verticalListSortingStrategy}
                        >
                            {rankedGroups.map((group, index) => {
                                const shelfLabels = [
                                    {
                                        label: 'Non-negotiable bowl',
                                        description:
                                            'Things you hope to guarantee yourself. Use this sparingly.',
                                        color: colors.pursuitGreen,
                                        emoji: '💎',
                                    },
                                    {
                                        label: 'Top shelf',
                                        description:
                                            'Things to strive hard for. These things will drive most of your career choices, so be selective.',
                                        color: colors.pursuitBlue,
                                        emoji: '🏆',
                                    },
                                    {
                                        label: 'Middle shelf',
                                        description:
                                            "Things to avoid total failure at. This shelf is good for the less-important qualities you feel you can't totally neglect.",
                                        color: colors.pursuitGold,
                                        emoji: '📚',
                                    },
                                    {
                                        label: 'Bottom shelf',
                                        description:
                                            'Things to keep in mind. Nice to have, but not at the expense of anything above it.',
                                        color: colors.pursuitBrown,
                                        emoji: '📦',
                                    },
                                    {
                                        label: 'Trash can',
                                        description:
                                            "Things to reject. Things you're actively choosing to deny",
                                        color: colors.pursuitOrange,
                                        emoji: '🗑️',
                                    },
                                ];

                                return (
                                    <div
                                        key={group.color}
                                        style={{
                                            border: `2px solid ${
                                                shelfLabels[index]?.color ||
                                                '#ccc'
                                            }`,
                                            borderRadius: '8px',
                                            padding: '8px',
                                            marginBottom: '16px',
                                        }}
                                    >
                                        {shelfLabels[index] && (
                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                            // @ts-ignore
                                            <ShelfLabel
                                                {...shelfLabels[index]}
                                            />
                                        )}
                                        <SortableItem
                                            group={{
                                                ...group,
                                                id: group.color,
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </SortableContext>
                    </DndContext>
                </>
            ),
        });

        return formStepsReturnArray;
    }, [
        onToggleImageUpload,
        isMobileScreenSize,
        selectedValues,
        customValue,
        onCustomValueInput,
        onAddCustomValue,
        valueGroups,
        sensors,
        handleDragEnd,
        rankedGroups,
        onToggleValue,
        getValueColor,
        onOpenColorPopup,
        onPhraseInput,
    ]);

    return isUploadingImages ? (
        <>
            <BackButtonWrapper>
                <Button onClick={onToggleImageUpload} isBackButton />
            </BackButtonWrapper>
            <Bumper $minHeight={spacing.xLarge} />
            {headerNode}
            <ValuesImageUpload onClose={onClose} />
        </>
    ) : (
        <>
            <SteppedForm
                headerNode={headerNode}
                formSteps={formSteps}
                onFinalSubmit={onSubmit}
                onChangeFormStep={onChangeFormStep}
                onClose={onClose}
                $isBottomAreaWhite
            />
            {popupValue && (
                <ColorPopup
                    value={popupValue}
                    onClose={onCloseColorPopup}
                    onSelectColor={onSelectColor}
                    currentColor={getValueColor(popupValue)}
                    position={popupPosition || undefined}
                />
            )}
        </>
    );
};
